import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { firestore } from '../firebase';
import { SHARED_PUN_ELEMENT_ID, parseSharedPun, type SharedPunPayload } from '../../shared/sharedPun';
import { countReaction, punFromDoc } from '../lib/puns';
import { phrasePrompt } from '../../shared/prompt';
import PageTitle from './PageTitle';

/**
 * The pun `punPreview` embedded in the document it served.
 *
 * Absent in two situations, both of which fall through to Firestore: `npm run
 * dev`, which has no functions in front of it, and a client-side navigation to
 * `/p/…` from inside the app, where the document was served for a different URL.
 */
function embeddedPun(): SharedPunPayload | null {
  return parseSharedPun(document.getElementById(SHARED_PUN_ELEMENT_ID)?.textContent);
}

/**
 * One pun, on the open web.
 *
 * The landing page for every shared link, and the only route outside the auth
 * gate other than sign-in.
 *
 * It renders from data the server embedded rather than from a Firestore read,
 * and that is not an optimisation. The author's display name lives in
 * `users/{userId}`, which `firestore.rules` gates behind `isSignedIn()` — so the
 * one visitor this page exists for, the one with no account, is exactly the one
 * who cannot fetch it. Publishing that collection instead would expose follow
 * lists, reminder times and time zones along with the name. `punPreview` already
 * resolves it with the Admin SDK to build the card, so it hands over the answer
 * in the same document.
 *
 * The link previews are not rendered here either — crawlers do not run
 * JavaScript, so the meta tags come from that same function.
 */
function SharedPun() {
  const { punId } = useParams();

  const [pun, setPun] = useState<SharedPunPayload | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  useEffect(() => {
    if (!punId) {
      setState('missing');
      return;
    }

    // The id is checked because a client-side navigation leaves the previous
    // page's payload in the document.
    const embedded = embeddedPun();
    if (embedded && embedded.id === punId) {
      setPun(embedded);
      setState('ready');
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const found = punFromDoc(await getDoc(doc(firestore, 'puns', punId)), 'SharedPun');
        if (cancelled) return;

        if (!found) {
          setState('missing');
          return;
        }

        // No author lookup on this path. `users` needs a signed-in reader, and
        // a page that fell back to Firestore is one where nobody handed us the
        // name — better an unattributed pun than a denied read.
        setPun({
          id: found.id,
          text: found.text,
          category: found.category,
          output: found.output,
          authorName: 'someone',
          crowns: countReaction(found, 'crown'),
        });
        setState('ready');
      } catch (error) {
        console.error('[SharedPun] could not load pun:', error);
        if (!cancelled) setState('missing');
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [punId]);

  if (state === 'loading') {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
        </div>
      </Shell>
    );
  }

  if (state === 'missing' || !pun) {
    return (
      <Shell>
        <PageTitle title="Pun not found" />
        <section className="rounded-2xl border-2 border-ink bg-white px-6 py-12 text-center shadow-pop">
          <p className="font-display text-2xl font-bold">That pun has left the building.</p>
          <p className="mx-auto mt-3 max-w-xs text-sm text-ink/60">
            The link may be wrong, or the pun may have been taken down.
          </p>
          <Callout />
        </section>
      </Shell>
    );
  }

  const { lead, output, join, category, tail } = phrasePrompt(pun.category, pun.output);

  return (
    <Shell>
      <PageTitle title={`A pun by ${pun.authorName}`} />

      <p className="mb-3 text-center text-sm text-ink/55">
        {lead} <span className="font-bold text-ink">{output}</span> {join}{' '}
        <span className="font-bold text-ink">{category}</span> {tail}
      </p>

      <article className="rounded-2xl border-2 border-ink bg-white px-6 py-7 shadow-pop">
        <p className="font-display text-2xl font-bold leading-snug">{pun.text}</p>

        <p className="mt-4 flex items-center justify-between text-xs text-ink/45">
          <span>by {pun.authorName}</span>
          {pun.crowns > 0 && (
            <span className="rounded-full border-2 border-ink bg-mustard px-2 py-0.5 font-bold tabular-nums text-ink">
              {pun.crowns} <span aria-hidden="true">&#128081;</span>
            </span>
          )}
        </p>
      </article>

      {/*
        The same PNG the link previews use. It is here because Instagram and
        every other Stories-shaped surface drops links entirely — an image is
        the only thing that survives the trip.

        A plain link rather than a fetch-and-share: the card is same-origin, so
        `download` genuinely downloads rather than navigating away.
      */}
      <p className="mt-4 text-center">
        <a
          href={`/card/${pun.id}.png`}
          download={`pun-and-done-${pun.id}.png`}
          className="text-sm text-ink/55 underline underline-offset-2 hover:text-ink"
        >
          Save as an image
        </a>
      </p>

      <Callout />
    </Shell>
  );
}

/**
 * The reason this page exists at all.
 *
 * A shared pun that does not ask the reader to play is a screenshot with extra
 * steps.
 */
function Callout() {
  return (
    <div className="mt-8 text-center">
      <p className="text-sm text-ink/60">One prompt a day. Everyone gets the same one.</p>
      <Link
        to="/signup"
        className="press mt-4 inline-block rounded-full border-2 border-ink bg-mustard px-7 py-3 font-display text-lg font-bold shadow-pop"
      >
        Write today&rsquo;s
      </Link>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper px-5 py-10 text-ink">
      <div className="mx-auto w-full max-w-lg">
        <Link
          to="/"
          className="mb-8 block text-center font-display text-3xl font-extrabold tracking-tight"
        >
          Pun &amp; Done
        </Link>
        {children}
      </div>
    </div>
  );
}

export default SharedPun;
